<script setup>
import notification from "@/utils/plugins/NotificationService";
/* eslint-disable no-undef */
import EmployeeSchedule from "@/components/shared/EmployeeSchedule.vue";

import { computed, ref, watch } from "vue";

const props = defineProps({
  selectedSchedule: { type: Object, default: null },
});

const emit = defineEmits(["close-dialog", "save"]);

const defaultSchedule = () => ({
  name: "",
  timeEnterMorning: "",
  timeExitMorning: "",
  timeEnterAfternoon: "",
  timeExitAfternoon: "",
  workHours: "",
  mon: true,
  tue: true,
  wed: true,
  thu: true,
  fri: true,
  days: [
    { day: "mon", workToday: true },
    { day: "tue", workToday: true },
    { day: "wed", workToday: true },
    { day: "thu", workToday: true },
    { day: "fri", workToday: true },
  ],
});

const hydrateSchedule = (value) => {
  const hydrated = {
    ...defaultSchedule(),
    ...(value || {}),
  };

  hydrated.days = [
    { day: "mon", workToday: hydrated.mon },
    { day: "tue", workToday: hydrated.tue },
    { day: "wed", workToday: hydrated.wed },
    { day: "thu", workToday: hydrated.thu },
    { day: "fri", workToday: hydrated.fri },
  ];

  return hydrated;
};

const schedule = ref(hydrateSchedule(props.selectedSchedule));
const showScheduleValidationErrors = ref(false);

const hasSelectedDay = (scheduleToValidate) => Boolean(
  scheduleToValidate.mon ||
  scheduleToValidate.tue ||
  scheduleToValidate.wed ||
  scheduleToValidate.thu ||
  scheduleToValidate.fri ||
  scheduleToValidate.days?.some((day) => day.workToday)
);

const hasCompleteTimeRange = (scheduleToValidate) => Boolean(
  (scheduleToValidate.timeEnterMorning && scheduleToValidate.timeExitMorning) ||
  (scheduleToValidate.timeEnterAfternoon && scheduleToValidate.timeExitAfternoon)
);

const canSave = computed(() =>
  Boolean(schedule.value.name) &&
  hasSelectedDay(schedule.value) &&
  hasCompleteTimeRange(schedule.value) &&
  !hasInvalidTimeRange(schedule.value) &&
  !hasInvalidTimeOrder(schedule.value)
);

const hasInvalidTimeRange = (scheduleToValidate) => {
  return Boolean(
    (scheduleToValidate.timeEnterMorning && !scheduleToValidate.timeExitMorning) ||
    (!scheduleToValidate.timeEnterMorning && scheduleToValidate.timeExitMorning) ||
    (scheduleToValidate.timeEnterAfternoon && !scheduleToValidate.timeExitAfternoon) ||
    (!scheduleToValidate.timeEnterAfternoon && scheduleToValidate.timeExitAfternoon)
  );
};

const toMinutes = (time) => {
  if (!time) {
    return null;
  }

  const [hours, minutes] = String(time).split(":").map(Number);
  return (hours * 60) + minutes;
};

const hasInvalidTimeOrder = (scheduleToValidate) => {
  const morningStart = toMinutes(scheduleToValidate.timeEnterMorning);
  const morningEnd = toMinutes(scheduleToValidate.timeExitMorning);
  const afternoonStart = toMinutes(scheduleToValidate.timeEnterAfternoon);
  const afternoonEnd = toMinutes(scheduleToValidate.timeExitAfternoon);

  return Boolean(
    (morningStart !== null && morningEnd !== null && morningStart >= morningEnd) ||
    (afternoonStart !== null && afternoonEnd !== null && afternoonStart >= afternoonEnd)
  );
};

watch(
  () => props.selectedSchedule,
  (value) => {
    schedule.value = hydrateSchedule(value);
  },
  { immediate: true }
);

const changeSchedule = (scheduleNew) => {
  const currentSchedule = schedule.value;
  Object.assign(schedule.value, {
    ...scheduleNew,
    id: scheduleNew.id || currentSchedule.id,
    "@id": scheduleNew["@id"] || currentSchedule["@id"],
    "@type": scheduleNew["@type"] || currentSchedule["@type"],
    name: currentSchedule.name,
  });
};

const normalizeSchedule = () => {
  const normalized = {
    ...schedule.value,
  };

  if (normalized.days) {
    normalized.days.forEach((day) => {
      normalized[day.day] = day.workToday;
    });
  }

  ["timeEnterMorning", "timeExitMorning", "timeEnterAfternoon", "timeExitAfternoon"].forEach((field) => {
    if (normalized[field] === "") {
      normalized[field] = null;
    }
  });

  delete normalized.days;

  return normalized;
};

const closeDialog = () => {
  emit("close-dialog");
};

const confirmDialog = () => {
  const normalized = normalizeSchedule();

  if (!normalized.name || !hasSelectedDay(normalized) || !hasCompleteTimeRange(normalized)) {
    showScheduleValidationErrors.value = true;
    notification.addNotification({
      title: "Horario",
      message: "Debe indicar nombre, al menos un día y un tramo horario completo",
      type: "error",
    });
    return;
  }

  if (hasInvalidTimeRange(normalized)) {
    showScheduleValidationErrors.value = true;
    notification.addNotification({
      title: "Horario",
      message: "Debe indicar entrada y salida para cada tramo horario informado",
      type: "error",
    });
    return;
  }
  if (hasInvalidTimeOrder(normalized)) {
    showScheduleValidationErrors.value = true;
    notification.addNotification({
      title: "Horario",
      message: "La hora de entrada debe ser anterior a la hora de salida",
      type: "error",
    });
    return;
  }
  showScheduleValidationErrors.value = false;

  const selectedScheduleId = props.selectedSchedule?.id || props.selectedSchedule?.["@id"]?.split("/").pop();

  if (selectedScheduleId && !normalized.id) {
    normalized.id = selectedScheduleId;
  }
  if (props.selectedSchedule?.["@id"] && !normalized["@id"]) {
    normalized["@id"] = props.selectedSchedule["@id"];
  }

  emit("save", {
    schedule: normalized,
  });
};
</script>

<template>
  <div>
    <div class="my-header schedule-dialog-header">
      <svg id="asterisk" xmlns="http://www.w3.org/2000/svg" width="724" height="1024" viewBox="0 0 724 1024">
        <path
          d="M0 499.8l59-181.7 332.5 108.6-59 180.8-332.5-107.7zM79.6 744.3l206.1-282.9 154.5 111.5-206.1 283.8-154.5-112.4zM266.9 167.3h191.1v350.3h-191.1v-350.3zM285.6 572.9l154.5-111.5 206.1 282.9-154.5 112.4-206.1-283.8zM332.5 426.8l332.5-108.6 59 181.7-331.6 107.7-59.9-180.8z"
        />
      </svg>
      <span class="text-header ml-5 mt-3">{{ schedule?.id ? 'Actualizar Horario' : 'Nuevo Horario' }}</span>
      <img
        class="close-icon pointer"
        src="@/assets/images/aspa_blue_small_entrada.png"
        alt=""
        @click="closeDialog"
      >
    </div>

    <div class="my-body schedule-dialog-body">
      <div class="row">
        <div class="col-md-12 el-col-xs-24">
          <div class="form-item">
            <label for="scheduleName" class="w-100">Nombre</label>
            <el-input
              id="scheduleName"
              v-model="schedule.name"
              class="w-100"
              placeholder="Nombre del horario"
              clearable
            />
          </div>
        </div>
      </div>

      <EmployeeSchedule
        :propSchedule="schedule"
        :show-validation-errors="showScheduleValidationErrors"
        @changeSchedule="changeSchedule"
      />

      <div class="row footer mt-4">
        <div class="col-md-12 d-flex justify-content-end gap-3 schedule-dialog-actions">
          <div class="col-md-3 el-col-xs-24">
            <button class="btn btn-block btn_yellow" type="button" @click="closeDialog">
              Cancelar
            </button>
          </div>
          <div class="col-md-3 el-col-xs-24">
            <button class="btn btn-block btn_red" type="button" :disabled="!canSave" @click="confirmDialog">
              {{ schedule?.id ? 'ACTUALIZAR' : 'CREAR' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.my-body.schedule-dialog-body {
  margin-right: 0;
}

@media (max-width: 767px) {
  .schedule-dialog-actions {
    flex-direction: column;
  }

  .schedule-dialog-actions .btn {
    width: 100%;
  }
}
</style>
